import { MailWarning } from 'lucide-react';
import React, { FormEvent, useEffect, useState } from 'react';
import { useRestClient } from '../../api/RestClientContext';
import { IdentityLoginToken } from '../../models/auth';
import LogoBar from '../../shared/components/LogoBar';
import { notify } from '../../shared/utils/notification';
import './LoginScreen.css';

interface LoginScreenProps {
    onAuthenticated: (token: string) => void;
}

const parseDurationMs = (duration: string): number => {
    const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(duration);
    if (!match) return 0;
    return ((Number(match[1] || 0) * 60 + Number(match[2] || 0)) * 60 + Number(match[3] || 0)) * 1000;
};

export const LoginScreen: React.FC<LoginScreenProps> = ({ onAuthenticated }) => {
    const client = useRestClient();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loginToken, setLoginToken] = useState<IdentityLoginToken | null>(null);
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!expiresAt) return;
        const updateRemainingTime = () => setRemainingSeconds(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
        updateRemainingTime();
        const timer = window.setInterval(updateRemainingTime, 1000);
        return () => window.clearInterval(timer);
    }, [expiresAt]);

    const requestCode = async (event: FormEvent) => {
        event.preventDefault();
        if (!email.trim() || !email.includes('@')) {
            notify.error('Введите корректный email');
            return;
        }
        setLoading(true);
        try {
            const token = await client.loginByEmail({ identity: email.trim(), type: 'EMAIL' });
            setLoginToken(token);
            setExpiresAt(Date.now() + parseDurationMs(token.duration));
        } finally {
            setLoading(false);
        }
    };

    const verifyCode = async (event: FormEvent) => {
        event.preventDefault();
        if (!/^\d{6}$/.test(code)) {
            notify.error('Введите 6-значный код');
            return;
        }
        if (remainingSeconds === 0) {
            notify.error('Срок действия кода истёк, запросите новый код');
            return;
        }
        setLoading(true);
        try {
            const result = await client.verifyEmailLogin({ rawToken: loginToken!.hash, code });
            localStorage.setItem('ogo.jwt', result.token);
            onAuthenticated(result.token);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="email-auth-screen">
            <LogoBar />
            <section className="email-auth-panel glass-ui">
                <span className="email-auth-kicker">OGO SECURE ACCESS</span>
                <h1>Вход по email</h1>
                <p>{loginToken ? `Код отправлен на ${email}` : 'Введите email, привязанный к вашему Telegram-аккаунту.'}</p>
                <div className="email-auth-notice" role="alert">
                    <MailWarning size={22} aria-hidden="true" />
                    <span>Письмо с кодом может попасть в папку «Спам». Адреса Google (Gmail) не поддерживаются.</span>
                </div>
                <p className="email-auth-bind-help">
                    Email еще не привязан? <a href="https://t.me/ogo_sa_bot" target="_blank" rel="noreferrer">Привязать email через Telegram-бота</a>
                </p>
                {!loginToken ? (
                    <form onSubmit={requestCode}>
                        <label htmlFor="login-email">Email</label>
                        <input id="login-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="you@example.com" required />
                        <button className="email-auth-submit" type="submit" disabled={loading}>{loading ? 'Отправляем...' : 'ПОЛУЧИТЬ КОД'}</button>
                    </form>
                ) : (
                    <form onSubmit={verifyCode}>
                        <label htmlFor="login-code">Код из письма</label>
                        <input id="login-code" inputMode="numeric" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" placeholder="000000" required />
                        <div className={`email-auth-timer ${remainingSeconds === 0 ? 'expired' : ''}`}>
                            {remainingSeconds > 0 ? `Код действителен еще ${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}` : 'Срок действия кода истек'}
                        </div>
                        {remainingSeconds > 0 && <button className="email-auth-submit" type="submit" disabled={loading}>{loading ? 'ПРОВЕРЯЕМ...' : 'ВОЙТИ'}</button>}
                        <button className="email-auth-link" type="button" onClick={() => { setLoginToken(null); setCode(''); setExpiresAt(null); setRemainingSeconds(0); }}>Изменить email</button>
                    </form>
                )}
            </section>
        </main>
    );
};
